import { Controller } from "@hotwired/stimulus"

const FIELD_TYPES = {
  checkbox: "checkbox",
  radio: "radio"
}

export default class extends Controller {
  static values = { submitSelector: String }

  connect() {
    this.namespace = "formCompletion"
    this.queueSync = this.queueSync.bind(this)

    this.element.addEventListener("input", this.queueSync)
    this.element.addEventListener("change", this.queueSync)

    if (window.$) {
      this.$selects = window.$(this.element).find("select.select2")
      this.$selects.on(
        `select2:select.${this.namespace} select2:unselect.${this.namespace} select2:clear.${this.namespace} change.${this.namespace}`,
        this.queueSync
      )
    }

    this.observer = new MutationObserver(this.queueSync)
    this.observer.observe(this.element, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["disabled", "required", "hidden", "aria-hidden"]
    })

    this.sync()
  }

  disconnect() {
    this.element.removeEventListener("input", this.queueSync)
    this.element.removeEventListener("change", this.queueSync)
    this.observer?.disconnect()
    this.$selects?.off(`.${this.namespace}`)

    if (this.frameId) {
      window.cancelAnimationFrame(this.frameId)
      this.frameId = null
    }
  }

  queueSync() {
    if (this.frameId) {
      window.cancelAnimationFrame(this.frameId)
    }

    this.frameId = window.requestAnimationFrame(() => {
      this.frameId = null
      this.sync()
    })
  }

  sync() {
    this.syncRichTextFields()

    const submit = this.submitElement()
    if (!submit) return

    const disabled = !this.formIsComplete()
    submit.disabled = disabled
    submit.setAttribute("aria-disabled", String(disabled))
  }

  submitElement() {
    return this.hasSubmitSelectorValue ?
      document.querySelector(this.submitSelectorValue) :
      this.element.closest(".modal")?.querySelector("[data-modal-submit='true']")
  }

  syncRichTextFields() {
    if (!window.tinymce) return

    this.element.querySelectorAll("textarea[id]").forEach((field) => {
      window.tinymce.get(field.id)?.save()
    })
  }

  formIsComplete() {
    const validatedGroups = new Set()

    return this.requiredFields().every((field) => {
      if (this.isCheckable(field)) {
        return this.groupIsValid(field, validatedGroups)
      }

      return this.fieldHasValue(field) && field.validity.valid
    })
  }

  requiredFields() {
    return Array.from(this.element.querySelectorAll("input, select, textarea"))
      .filter((field) => field.required && !field.disabled)
  }

  isCheckable(field) {
    return [FIELD_TYPES.checkbox, FIELD_TYPES.radio].includes(field.type)
  }

  groupIsValid(field, validatedGroups) {
    const groupName = field.name

    if (!groupName) return field.checked
    if (validatedGroups.has(groupName)) return true

    validatedGroups.add(groupName)

    const groupFields = Array.from(this.element.querySelectorAll("input"))
      .filter((candidate) =>
        candidate.name === groupName &&
        candidate.type === field.type &&
        !candidate.disabled
      )

    return groupFields.some((candidate) => candidate.checked)
  }

  fieldHasValue(field) {
    if (field instanceof HTMLSelectElement) {
      return field.value !== ""
    }

    return field.value.trim() !== ""
  }
}